// src/dashboard/ChartsPanel.jsx
import React, { useMemo, useState, useEffect } from "react";
import {
  ResponsiveContainer,
  LineChart as RCLineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  BarChart as RCBarChart,
  Bar,
  PieChart as RCPieChart,
  Pie,
  Cell,
} from "recharts";
import { BarChart3, PieChart, Users, Receipt, Calculator, LineChart } from "lucide-react";

/* ---------- tiny “section header” + separator (same look as KPI) ---------- */
const SectionHeader = ({ title, icon: Icon = BarChart3 }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10, paddingBottom: 8 }}>
    <div style={{ width: 36, height: 36, borderRadius: 10, background: "#f1f5f9", display: "grid", placeItems: "center" }}>
      <Icon size={18} />
    </div>
    <div style={{ fontSize: "1rem", fontWeight: 800, color: "#0f172a" }}>{title}</div>
  </div>
);
const Separator = () => <div style={{ height: 1, width: "100%", background: "#e5e7eb", marginBottom: 10 }} />;

/* ---------- layout helper (2 cols -> 1 on small screens) ---------- */
const useWindowWidth = () => {
  const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);
  useEffect(() => {
    const onR = () => setW(window.innerWidth);
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, []);
  return w;
};

/* ---------- numeric helpers ---------- */
const toNumber = (v) =>
  v == null
    ? 0
    : typeof v === "number"
    ? v
    : parseFloat(String(v).replace(/\s/g, "").replace(",", ".").replace(/[^\d.-]/g, "")) || 0;

/* SALES: net + VAT due (as you had) */
const netFromRow = (r) =>
  toNumber(r.K_15) +
  toNumber(r.K_19) +
  toNumber(r.K_10) +
  toNumber(r.K_21) +
  toNumber(r.K_17) +
  toNumber(r.K_11) +
  toNumber(r.K_22);

const vatDueFromRow = (r) => toNumber(r.K_16) + toNumber(r.K_20) + toNumber(r.K_18);

/* PURCHASES: VAT input */
const vatInFromRow = (r) => toNumber(r.K_43);

/* ---------- SAFE date parsing (handles dd.mm.yyyy etc.) ---------- */
const parseAnyDate = (v) => {
  if (!v) return null;
  if (v instanceof Date && !isNaN(v)) return v;

  const s = String(v).trim();
  if (!s) return null;

  // dd.mm.yyyy
  let m = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})/);
  if (m) {
    const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
    return isNaN(d) ? null : d;
  }

  // dd/mm/yyyy
  m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (m) {
    const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
    return isNaN(d) ? null : d;
  }

  // yyyymmdd
  m = s.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return isNaN(d) ? null : d;
  }

  // ISO / other formats that JS parses reliably
  const d = new Date(s);
  return isNaN(d) ? null : d;
};

/* ---------- KPI-like period logic: Rok/Miesiac first, then fallback dates ---------- */
const getYearMonth = (row) => {
  // Prefer explicit period from JPK
  if (row?.Rok && row?.Miesiac) {
    const y = Number(row.Rok);
    const mIndex = Number(row.Miesiac) - 1;
    if (isFinite(y) && isFinite(mIndex) && mIndex >= 0 && mIndex <= 11) return { y, mIndex };
  }

  // Fallback to date fields (choose order to match your data expectations)
  const d =
    parseAnyDate(row?.DataZakupu) ||
    parseAnyDate(row?.DataWystawienia) ||
    parseAnyDate(row?.DataWplywu) ||
    parseAnyDate(row?.DataWytworzeniaJPK);

  return d ? { y: d.getFullYear(), mIndex: d.getMonth() } : null;
};

const getDate = (row) => {
  const ym = getYearMonth(row);
  return ym ? new Date(ym.y, ym.mIndex, 1) : null;
};

const monthPL = (mIndex) => new Date(2000, mIndex, 1).toLocaleString("pl-PL", { month: "short" });
const fmtPLN0 = (n) => (n ?? 0).toLocaleString("pl-PL", { maximumFractionDigits: 0 });

/* ---------- linear regression trend series (returns Y fitted for x=0..n-1) ---------- */
const linearTrend = (ys) => {
  const n = ys.length;
  if (n < 2) return Array(n).fill(null);
  const xs = ys.map((_, i) => i);
  const sumX = xs.reduce((a, b) => a + b, 0);
  const sumY = ys.reduce((a, b) => a + b, 0);
  const sumXY = xs.reduce((s, x, i) => s + x * ys[i], 0);
  const sumXX = xs.reduce((s, x) => s + x * x, 0);
  const denom = n * sumXX - sumX * sumX || 1;
  const a = (n * sumXY - sumX * sumY) / denom; // slope
  const b = (sumY - a * sumX) / n; // intercept
  return xs.map((x) => a * x + b);
};

/* ---------- small card ---------- */
const Card = ({ title, icon: Icon = LineChart, children }) => (
  <div
    style={{
      border: "1px solid #e5e7eb",
      borderRadius: 12,
      background: "#fff",
      boxShadow: "0 4px 10px rgba(0,0,0,0.06)",
      padding: 14,
      minHeight: 450,
      display: "flex",
      flexDirection: "column",
      gap: 10,
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{ width: 28, height: 28, borderRadius: 8, background: "#f1f5f9", display: "grid", placeItems: "center" }}>
        <Icon size={16} />
      </div>
      <div style={{ fontWeight: 800, color: "#0f172a" }}>{title}</div>
    </div>
    <div style={{ flex: 1, minHeight: 200 }}>{children}</div>
  </div>
);

const ChartsPanel = ({ salesData = [], purchasesData = [] }) => {
  const width = useWindowWidth();

  /* pick the latest (year, month) that exists in either dataset (using KPI-like logic) */
  const { year, maxMonthIndex } = useMemo(() => {
    const all = [
      ...salesData.map(getYearMonth).filter(Boolean),
      ...purchasesData.map(getYearMonth).filter(Boolean),
    ];

    if (!all.length) {
      const now = new Date();
      return { year: now.getFullYear(), maxMonthIndex: now.getMonth() };
    }

    const maxYear = Math.max(...all.map((x) => x.y));
    const maxMonth = Math.max(...all.filter((x) => x.y === maxYear).map((x) => x.mIndex));
    return { year: maxYear, maxMonthIndex: isFinite(maxMonth) ? maxMonth : 11 };
  }, [salesData, purchasesData]);

  const months = Array.from({ length: maxMonthIndex + 1 }, (_, i) => i); // 0..max month with data

  const { monthly, vatMonthly, customersMonthly, invoicesMonthly, topBuyers, topSuppliers } = useMemo(() => {
    const mSalesSum = new Map();
    const mPurchSum = new Map();
    const mVatDue = new Map();
    const mVatIn = new Map();
    const mSalesInv = new Map(); // ym -> Set
    const mCustSet = new Map();

    const ensureSet = (map, key) => (map.has(key) ? map.get(key) : (map.set(key, new Set()), map.get(key)));
    const ymKey = (y, mIndex) => `${y}-${String(mIndex + 1).padStart(2, "0")}`;

    // SALES per month
    salesData.forEach((r) => {
      const ym = getYearMonth(r);
      if (!ym || ym.y !== year) return;

      const key = ymKey(ym.y, ym.mIndex);
      mSalesSum.set(key, (mSalesSum.get(key) || 0) + netFromRow(r));
      mVatDue.set(key, (mVatDue.get(key) || 0) + vatDueFromRow(r));

      if (r.DowodSprzedazy) ensureSet(mSalesInv, key).add(r.DowodSprzedazy);

      const cust = r.NrKontrahenta || r.NazwaKontrahenta;
      if (cust) ensureSet(mCustSet, key).add(cust);
    });

    // PURCHASES per month
    purchasesData.forEach((r) => {
      const ym = getYearMonth(r);
      if (!ym || ym.y !== year) return;

      const key = ymKey(ym.y, ym.mIndex);
      mPurchSum.set(key, (mPurchSum.get(key) || 0) + toNumber(r.K_42));
      mVatIn.set(key, (mVatIn.get(key) || 0) + vatInFromRow(r));
    });

    const monthly = months.map((mIndex) => {
      const key = ymKey(year, mIndex);
      return {
        mIndex,
        mLabel: monthPL(mIndex),
        sales: mSalesSum.get(key) || 0,
        purchases: mPurchSum.get(key) || 0,
      };
    });

    const vatMonthly = months.map((mIndex) => {
      const key = ymKey(year, mIndex);
      return { mLabel: monthPL(mIndex), due: mVatDue.get(key) || 0, input: mVatIn.get(key) || 0 };
    });

    const customersMonthly = months.map((mIndex) => {
      const key = ymKey(year, mIndex);
      const s = mCustSet.get(key);
      return { mLabel: monthPL(mIndex), customers: s ? s.size : 0 };
    });

    const invoicesMonthly = months.map((mIndex) => {
      const key = ymKey(year, mIndex);
      const s = mSalesInv.get(key);
      return { mLabel: monthPL(mIndex), salesInvoices: s ? s.size : 0 };
    });

    // Top 5 buyers/suppliers (YTD in selected year)
    const buyers = new Map();
    salesData.forEach((r) => {
      const ym = getYearMonth(r);
      if (!ym || ym.y !== year) return;
      const k = r.NazwaKontrahenta || r.NrKontrahenta || "—";
      buyers.set(k, (buyers.get(k) || 0) + netFromRow(r));
    });
    const topBuyers = [...buyers.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, value]) => ({ name, value }));

    const suppliers = new Map();
    purchasesData.forEach((r) => {
      const ym = getYearMonth(r);
      if (!ym || ym.y !== year) return;
      const k = r.NazwaDostawcy || r.NrDostawcy || "—";
      suppliers.set(k, (suppliers.get(k) || 0) + toNumber(r.K_42));
    });
    const topSuppliers = [...suppliers.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, value]) => ({ name, value }));

    return { monthly, vatMonthly, customersMonthly, invoicesMonthly, topBuyers, topSuppliers };
  }, [salesData, purchasesData, year, months]);

  // Trend lines (linear regression)
  const trendSales = linearTrend(monthly.map((d) => d.sales));
  const trendPurchases = linearTrend(monthly.map((d) => d.purchases));
  const monthlyWithTrends = monthly.map((d, i) => ({
    ...d,
    salesTrend: trendSales[i],
    purchasesTrend: trendPurchases[i],
  }));

  const PIE_COLORS = ["#16a34a", "#2563eb", "#f59e0b", "#dc2626", "#10b981"];
  const GREEN = "#16a34a";
  const BLUE = "#2563eb";
  const RED = "#ef4444";

  const gridStyle = {
    display: "grid",
    gap: 14,
    gridTemplateColumns: width < 900 ? "1fr" : "repeat(2, 1fr)",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <SectionHeader title={`Wykresy — Rok ${year}`} icon={BarChart3} />
      <Separator />

      <div style={gridStyle}>
        {/* 1. Sprzedaż i zakupy miesięcznie (YTD) — słupki + linia trendu */}
        <Card title="Sprzedaż i zakupy miesięcznie (YTD) — słupki + linia trendu" icon={BarChart3}>
          <ResponsiveContainer width="100%" height="100%">
            <RCBarChart data={monthlyWithTrends} margin={{ left: 0, right: 8, top: 8, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="mLabel" />
              <YAxis tickFormatter={(v) => fmtPLN0(v)} />
              <Tooltip formatter={(v) => fmtPLN0(v)} />
              <Legend />
              <Bar dataKey="sales" name="Sprzedaż" fill={GREEN} />
              <Bar dataKey="purchases" name="Zakupy" fill={BLUE} />
              <Line type="monotone" dataKey="salesTrend" name="Trend sprzedaż" stroke={GREEN} strokeDasharray="4 4" dot={false} />
              <Line type="monotone" dataKey="purchasesTrend" name="Trend zakupy" stroke={BLUE} strokeDasharray="4 4" dot={false} />
            </RCBarChart>
          </ResponsiveContainer>
        </Card>

        {/* 2. Top 5 Nabywców (YTD) — kołowy */}
        <Card title="Top 5 nabywców w tym roku (YTD)" icon={PieChart}>
          {topBuyers.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <RCPieChart>
                <Tooltip formatter={(v) => fmtPLN0(v)} />
                <Legend />
                <Pie data={topBuyers} dataKey="value" nameKey="name" outerRadius="80%" label={({ name, value }) => `${name} (${fmtPLN0(value)} zł)`}>
                  {topBuyers.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
              </RCPieChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ padding: 16, color: "#64748b" }}>Brak danych.</div>
          )}
        </Card>

        {/* 3. Top 5 Dostawców (YTD) — kołowy */}
        <Card title="Top 5 dostawców w tym roku (YTD)" icon={PieChart}>
          {topSuppliers.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <RCPieChart>
                <Tooltip formatter={(v) => fmtPLN0(v)} />
                <Legend />
                <Pie data={topSuppliers} dataKey="value" nameKey="name" outerRadius="80%" label={({ name, value }) => `${name} (${fmtPLN0(value)} zł)`}>
                  {topSuppliers.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[(i + 1) % PIE_COLORS.length]} />
                  ))}
                </Pie>
              </RCPieChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ padding: 16, color: "#64748b" }}>Brak danych.</div>
          )}
        </Card>

        {/* 4. VAT należny vs VAT naliczony (YTD) — słupkowy */}
        <Card title="VAT należny vs VAT naliczony (miesięcznie, YTD)" icon={Calculator}>
          <ResponsiveContainer width="100%" height="100%">
            <RCBarChart data={vatMonthly} margin={{ left: 0, right: 8, top: 8, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="mLabel" />
              <YAxis tickFormatter={(v) => fmtPLN0(v)} />
              <Tooltip formatter={(v) => fmtPLN0(v)} />
              <Legend />
              <Bar dataKey="due" name="Należny" fill={RED} />
              <Bar dataKey="input" name="Naliczony" fill={BLUE} />
            </RCBarChart>
          </ResponsiveContainer>
        </Card>

        {/* 5. Liczba klientów (miesięcznie, YTD) */}
        <Card title="Liczba klientów (miesięcznie, YTD)" icon={Users}>
          <ResponsiveContainer width="100%" height="100%">
            <RCLineChart data={customersMonthly} margin={{ left: 0, right: 8, top: 8, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="mLabel" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="customers" name="Klienci" stroke={GREEN} dot={false} />
            </RCLineChart>
          </ResponsiveContainer>
        </Card>

        {/* 6. Liczba faktur sprzedaży (miesięcznie, YTD) */}
        <Card title="Liczba faktur sprzedaży (miesięcznie, YTD)" icon={Receipt}>
          <ResponsiveContainer width="100%" height="100%">
            <RCBarChart data={invoicesMonthly} margin={{ left: 0, right: 8, top: 8, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="mLabel" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="salesInvoices" name="Faktury" fill={BLUE} />
            </RCBarChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
};

export default ChartsPanel;
